import { DepartureGroup } from '@/lib/departures.ts'
import { Departure, WidgetTranslations } from '@/data.ts'
import { Trans, useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import { cn } from '@/lib/cn.ts'
import { ChevronDown } from '@/icons/chevrons.tsx'
import { HarbourIcon } from '@/icons/harbour.tsx'
import { Badge } from '@/components/badge.tsx'
import { ShipIcon } from '@/icons/ship.tsx'
import { PopoverTooltip } from '@/components/popover.tsx'
import { parseISO } from 'date-fns'
import { DEFAULT_LANGUAGE } from '@/i18n.ts'

function FullyBookedHeaderLine ({ departureGroup, className }: {
  departureGroup: DepartureGroup
  className?: string
}): JSX.Element {
  const { t } = useTranslation('widget')
  const count = departureGroup.departures.length
  const beforeDate = departureGroup.next_start_date != null ? parseISO(departureGroup.next_start_date) : null
  const afterDate = departureGroup.previous_start_date != null ? parseISO(departureGroup.previous_start_date) : null
  if (afterDate == null && beforeDate == null) {
    return (
      <div className={className}>
        <span>
          <Trans
            t={t} i18nKey='fully_booked.only' count={count}
          >
            <strong>{count} fully booked dates</strong>
            <span> found</span>
          </Trans>
        </span>
      </div>
    )
  } else if (afterDate == null) {
    return (
      <div className={className}>
        <span>
          <Trans
            t={t} i18nKey='fully_booked.before' count={count}
            values={{ date: beforeDate }}
          >
            <strong>{count} fully booked dates</strong>
            <span> found before </span>
            <strong>{t('format.date', { value: beforeDate })}</strong>
          </Trans>
        </span>
      </div>
    )
  } else if (beforeDate == null) {
    return (
      <div className={className}>
        <span>
          <Trans
            t={t} i18nKey='fully_booked.after' count={count}
            values={{ date: afterDate }}
          >
            <strong>{count} fully booked dates</strong>
            <span> found after </span>
            <strong>{t('format.date', { value: afterDate })}</strong>
          </Trans>
        </span>
      </div>
    )
  } else {
    return (
      <div className={className}>
        <span
          className='hidden group-has-[+_.hidden]:inline'
        >
          <Trans
            t={t} i18nKey='fully_booked.after' count={count}
            values={{ date: afterDate }}
          >
            <strong>{count} fully booked dates</strong>
            <span> found after </span>
            <strong>{t('format.date', { value: afterDate })}</strong>
          </Trans>
        </span>
        <span
          className='group-has-[+_.hidden]:hidden'
        >

          <Trans
            t={t} i18nKey='fully_booked.between' count={count}
            values={{ beforeDate, afterDate }}
          >
            <strong>{count} fully booked dates</strong>
            <span> found between </span>
            <strong>{t('format.date', { value: afterDate })}</strong>
            <span> &amp; </span>
            <strong>{t('format.date', { value: beforeDate })}</strong>
          </Trans>
        </span>
      </div>
    )
  }
}

export function FullyBookedDepartureLine ({ departure, translations }: {
  translations: WidgetTranslations
  departure: Departure
}): JSX.Element {
  const start = useMemo(() => parseISO(departure.start_date), [departure.start_date])
  const end = useMemo(() => parseISO(departure.end_date), [departure.end_date])
  const { i18n, t } = useTranslation('widget')

  const portOfDepartureName = useMemo(() => {
    const harbour = translations.harbours.find(harbour => harbour.id === departure.port_of_departure.id)
    const translation = harbour?.translations.find(translation => translation.language === i18n.language) ?? harbour?.translations.find(translation => translation.language === DEFAULT_LANGUAGE)
    return translation?.name ?? harbour?.name ?? departure.port_of_departure.name
  }, [departure, i18n.language, translations.harbours])

  const shipTranslation = useMemo(() => {
    const ship = translations.ships?.find(ship => ship.id === departure.ship.id)
    return ship?.translations.find(translation => translation.language === i18n.language) ?? ship?.translations.find(translation => translation.language === DEFAULT_LANGUAGE)
  }, [departure, i18n.language, translations.ships])

  return (
    <div className='py-4 @desktop:col-span-(--cols) grid grid-cols-subgrid'>

      <div
        className='flex justify-between pb-2 border-b border-b-brand-primary-light @desktop:contents'
      >
        <div className='flex flex-col gap-2 items-start'>
          <div>{t('format.weekday', { ns: 'widget', value: start })}</div>
          <div className='text-brand-primary font-bold'>{t('format.date', { ns: 'widget', value: start })}</div>
        </div>
        <div className='flex flex-col gap-2 items-start'>
          <div>{t('format.weekday', { ns: 'widget', value: end })}</div>
          <div className='text-brand-primary font-bold'>{t('format.date', { ns: 'widget', value: end })}</div>
        </div>
      </div>
      <div className='flex justify-between @desktop:flex-col text-body-xs @desktop:text-base'>
        <div className='flex gap-1 items-start'>
          <div className='flex items-center h-lh'>
            <HarbourIcon className='text-brand-primary' />
          </div>
          {portOfDepartureName}
        </div>
        <div>
          {departure.tour.guidance?.map(guidance => (
            <Badge key={guidance}>{t(guidance, { keyPrefix: 'guidance_types' })}</Badge>
          ))}
        </div>
      </div>
      <div className='hidden @desktop:block' />
      <div className='flex justify-between @desktop:flex-col text-sm @desktop:text-base'>
        <div className='flex gap-1 items-start'>
          <div className='flex items-center h-lh'>
            <ShipIcon className='text-brand-primary' />
          </div>
          {shipTranslation?.name ?? departure.ship.name}
        </div>
        <div>
          {departure.status === 'on_request'
            ? (
                <PopoverTooltip
                  title={t('tooltips.on_request.title')}
                  description={t('tooltips.on_request.description')}
                >
                  <Badge variant='info'>{t(departure.status, {
                    keyPrefix: 'departure_statuses'
                  })}
                  </Badge>
                </PopoverTooltip>
              )
            : (
                <Badge variant='success'>{t(departure.status, {
                  keyPrefix: 'departure_statuses'
                })}
                </Badge>
              )}
        </div>
      </div>
      <div className='hidden @desktop:block' />
      <div className='hidden @desktop:block' />
    </div>
  )
}

export function FullyBookedListGroup ({
  departureGroup,
  translations,
  className
}: {
  departureGroup: DepartureGroup
  translations: WidgetTranslations
  className?: string
}): JSX.Element {
  const { t } = useTranslation('widget')
  const [open, setOpen] = useState(false)
  return (
    <div
      className={cn('grayscale-86 fully-booked-group relative @desktop:col-span-(--cols) grid grid-cols-subgrid divide-y divide-brand-primary-light border border-dashed border-base-3 bg-brand-primary-lightest rounded-xl opacity-85', className)}
    >
      <div
        className='@desktop:col-span-(--cols) flex justify-between cursor-pointer p-4'
        onClick={() => setOpen(open => !open)}
      >
        <FullyBookedHeaderLine departureGroup={departureGroup} className='' />
        <button
          className='relative hidden @desktop:flex items-center cursor-pointer' type='button'
          title={open ? t('button_title.close_departure', 'Collapse departure block') : t('button_title.open_departure', 'Expand departure block')}
        >
          <ChevronDown className={cn('size-4 text-brand-primary transition-transform duration-200', {
            'rotate-180': open
          })}
          />
          <div className='absolute top-1/2 left-1/2 -m-3 size-6' />
        </button>
      </div>
      {open && (
        <div className='px-4 divide-y divide-brand-primary-light @desktop:col-span-(--cols) grid grid-cols-subgrid'>
          {departureGroup.departures.map(departure => (
            <FullyBookedDepartureLine departure={departure} translations={translations} key={departure.id} />
          ))}
        </div>
      )}
    </div>
  )
}
